import { useMemo, useState } from 'react'
import { Button, Descriptions, Drawer, Select, Space, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { Can } from '@/shared/auth/Can'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { ClosedHiddenTag } from '@/shared/ui/table/ClosedHidden'
import { useClosedHidden } from '@/shared/ui/table/useClosedHidden'
import { STATE_ALL, STATE_OPEN_ONLY } from '@/shared/ui/table/closedFilter'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { SiteRef } from '@/features/sites/SiteRef'
import { useSiteIndex } from '@/features/sites/useSiteIndex'
import { EquipmentActions } from '@/features/inventory/EquipmentActions'
import { EquipmentFormModal } from '@/features/inventory/EquipmentFormModal'
import { EquipmentOwnerModal } from '@/features/inventory/EquipmentOwnerModal'
import { EquipmentOwnerRef } from '@/features/inventory/EquipmentOwnerRef'
import { WarehouseRef } from '@/features/inventory/WarehouseRef'
import {
  EQUIPMENT_STATES,
  EQUIPMENT_TYPES,
  countClosedEquipment,
  fetchEquipmentPage,
  type Equipment,
  type EquipmentState,
  type EquipmentStateFilter,
  type EquipmentType,
} from '@/features/inventory/equipmentApi'

/**
 * Rang HOLATNING MA'NOSINI ko'rsatadi:
 *   · `IN_STOCK`  — ko'k, ishlatishga tayyor
 *   · `ASSIGNED`  — yashil, ish joyida
 *   · `FAULTY`    — qizil, ARALASHUV kerak
 *   · `DECOMMISSIONED` — kulrang, hisobdan chiqqan (yozuv tarix uchun qoladi)
 */
const STATE_COLOR: Record<EquipmentState, string> = {
  IN_STOCK: 'blue',
  ASSIGNED: 'green',
  FAULTY: 'red',
  // ⚠️ `FAULTY` bilan bir xil rang EMAS: nosoz jihoz omborda turibdi va
  // ta'mirlanishi mumkin, yo'qolgani esa fizik jihatdan yo'q — ular boshqa
  // ish talab qiladi (`EquipmentState` javadoc'i)
  LOST: 'volcano',
  DECOMMISSIONED: 'default',
}

/**
 * Jihozlar (inventar).
 *
 * <h2>Nima uchun bu sahifa bor</h2>
 * Router, ONU va modem billing uchun ko'rinmas: RADIUS ularni bilmaydi.
 * Lekin ular pul turadi, sinadi va yo'qoladi. Bu ro'yxat ularni seriya raqami
 * bo'yicha hisobga oladi va — eng muhimi — JOYLASHUVGA bog'laydi.
 *
 * <h2>⚠️ "Joylashuv" ustuni hech qachon bo'sh qolmaydi</h2>
 * `SiteRef` to'rt holatni alohida chizadi: ko'rsatilmagan, yuklanmoqda,
 * xarita mavjud emas, va topilmadi. Bo'sh katak ularning hammasini bir xil
 * ko'rsatardi — aynan shu funksiya oldini olmoqchi bo'lgan nosozlik.
 *
 * <h2>⚠️ Seriya raqami — o'zlik, uni tahrirlab bo'lmaydi</h2>
 * Sabab `equipmentApi.EquipmentDraft` da.
 *
 * <h2>⚠️ HISOBDAN CHIQARILGANLAR SUKUT BO'YICHA KO'RINMAYDI (ADR-033)</h2>
 * 2026-08-21 da server `includeClosed` bayrog'ini oldi
 * (`EquipmentResource#search`) va ilgari bu yerda yozilgan to'siq —
 * «yakka `EquipmentState state`, ya'ni DECOMMISSIONED'siz hammasi
 * so'rovi YO'Q» — yopildi. Panel bayroqni HAR so'rovda ochiq yuboradi
 * ({@code equipmentListParams}).
 *
 * <p>⚠️ «Yopiq» bu yerda FAQAT {@code DECOMMISSIONED}. {@code FAULTY}
 * va {@code LOST} QOLADI: nosozni ta'mirlash, yo'qolganini qidirish
 * kerak — ikkalasi ham ochiq ish, tugagan yozuv emas.
 *
 * <p>⚠️ Ro'yxat SAHIFALANADI, ya'ni mijozda kesish taqiqlanadi: u faqat
 * joriy sahifani toraytirardi, jami son esa o'zgarmasdi. Filtr
 * SERVERGA boradi, torayish esa «5 / 8» nishoni bilan ekranda
 * aytiladi.
 */
export function InventoryPage() {
  const { t } = useTranslation()

  const [selected, setSelected] = useState<Equipment | null>(null)
  const [editing, setEditing] = useState<Equipment | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  /*
    Egani boshqarish oynasi SAHIFADA turadi, `EquipmentActions` da emas:
    uni ikki joydan ochish mumkin (kartochka va tahrirlash oynasi), ikki
    nusxa esa jimgina ajralib ketardi (`EquipmentActions` izohi).
  */
  const [ownerOpen, setOwnerOpen] = useState(false)
  const [ownerTarget, setOwnerTarget] = useState<Equipment | null>(null)

  const [type, setType] = useState<EquipmentType | null>(null)
  /*
    ⚠️ Sukut qiymat `null` («hammasi») EMAS: ADR-033 bo'yicha ro'yxat
    hisobdan chiqarilgan jihozlarsiz ochiladi.
  */
  const [state, setState] = useState<EquipmentStateFilter>(STATE_OPEN_ONLY)
  const [siteId, setSiteId] = useState<string | null>(null)

  const { sites, loading: sitesLoading } = useSiteIndex()

  const filter = useMemo(() => ({ type, state, siteId }), [type, state, siteId])
  const fetchPage = useMemo(() => fetchEquipmentPage(filter), [filter])

  /*
    ⚠️ Yashiringan qatorlar soni SERVERDAN: sahifalangan ro'yxatda
    mijozdagi hisob faqat joriy sahifani ko'rardi.
  */
  const { state: tableState, patch: patchTable } = useTableUrlState()
  const countClosed = useMemo(() => countClosedEquipment(filter), [filter])
  const hiddenCounts = useClosedHidden<Equipment>({
    queryKey: [...queryKeys.inventory.equipment, filter],
    state: tableState,
    fetchPage,
    countClosed,
    enabled: state === STATE_OPEN_ONLY,
  })

  /**
   * ⚠️ Holat almashganda sahifa BIRINCHIGA qaytariladi — holat
   * sahifaning O'Z holatida, sahifa raqami esa manzilda va ular
   * avtomatik bog'lanmaydi. Usiz 3-sahifada turib «Barcha holatlar»
   * bosilsa ekran BO'SH chiqardi.
   */
  const changeState = (next: EquipmentStateFilter) => {
    setState(next)
    patchTable({ page: 1 })
  }

  const columns = useMemo<ColumnsType<Equipment>>(
    () => [
      {
        key: 'serialNumber',
        title: t('inventory.serialNumber'),
        width: 200,
        render: (_, row) => (
          <Typography.Text strong copyable={{ text: row.serialNumber }}>
            {row.serialNumber}
          </Typography.Text>
        ),
      },
      {
        key: 'type',
        title: t('inventory.type'),
        width: 120,
        render: (_, row) => t(`equipmentType.${row.type}`),
      },
      {
        key: 'model',
        title: t('inventory.model'),
        width: 180,
        render: (_, row) => row.model ?? t('common.dash'),
      },
      {
        key: 'state',
        title: t('inventory.state'),
        width: 140,
        render: (_, row) => (
          <Tag color={STATE_COLOR[row.state]}>{t(`equipmentState.${row.state}`)}</Tag>
        ),
      },
      {
        key: 'site',
        title: t('inventory.site'),
        width: 230,
        render: (_, row) => <SiteRef siteId={row.siteId} />,
      },
      {
        key: 'owner',
        title: t('inventory.owner'),
        width: 240,
        // ⚠️ ESKI IZOH BEKOR QILINDI (XSB-003): "egasi NOM emas, TUR + id,
        // chunki panel id ni nomga o'girishni bilmaydi". Endi biladi —
        // `EquipmentOwnerRef` har tur uchun to'g'ri so'rovni yuboradi va
        // huquq yo'q / topilmadi holatlarini ALOHIDA chizadi.
        render: (_, row) => (
          <EquipmentOwnerRef ownerType={row.ownerType} ownerId={row.ownerId} showHint={false} />
        ),
      },
      {
        key: 'actions',
        title: '',
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, row) => (
          // ⚠️ Amal huquqsiz — kartochka o'qish uchun, `onRow` ham aynan
          // shuni qiladi. Menyu esa "bu qator bilan nima qilsa bo'ladi"
          // savolini boshqa jadvallar bilan BIR XIL joyda javoblaydi.
          <RowActions
            items={[{ key: 'open', label: t('inventory.open'), onClick: () => setSelected(row) }]}
          />
        ),
      },
    ],
    [t],
  )

  return (
    <>
      <PageHeader
        title={t('nav.inventory')}
        description={t('pageIntro.inventory')}
        extra={
          <Can permission="inventory.equipment.write">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('inventory.createTitle')}
            </Button>
          </Can>
        }
      />

      <Space wrap style={{ marginBottom: 16 }}>
        <Select<string>
          style={{ width: 170 }}
          value={type ?? 'ALL'}
          onChange={(value) => setType(value === 'ALL' ? null : (value as EquipmentType))}
          options={[
            { value: 'ALL', label: t('inventory.allTypes') },
            ...EQUIPMENT_TYPES.map((value) => ({ value, label: t(`equipmentType.${value}`) })),
          ]}
        />
        {/*
          ⚠️ Sukut band «Barcha holatlar» EMAS, «Yopilganlarsiz»: bo'sh
          yoki «hammasi» ko'rinishi ro'yxatning torayganini yashirardi.
          «Barcha holatlar» ro'yxat OXIRIDA qoladi va u — hisobdan
          chiqarilgan jihozni ko'rishning yagona yo'li.
        */}
        <Tooltip title={t('dataTable.closedHiddenHint')}>
          <Select<EquipmentStateFilter>
            style={{ width: 200 }}
            aria-label={t('inventory.state')}
            value={state}
            onChange={changeState}
            options={[
              { value: STATE_OPEN_ONLY, label: t('dataTable.stateOpenOnly') },
              ...EQUIPMENT_STATES.map((value) => ({ value, label: t(`equipmentState.${value}`) })),
              { value: STATE_ALL, label: t('dataTable.stateAll') },
            ]}
          />
        </Tooltip>
        <ClosedHiddenTag counts={hiddenCounts} onShowAll={() => changeState(STATE_ALL)} />
        <Select<string>
          style={{ width: 240 }}
          showSearch
          optionFilterProp="label"
          loading={sitesLoading}
          value={siteId ?? 'ALL'}
          onChange={(value) => setSiteId(value === 'ALL' ? null : value)}
          options={[
            { value: 'ALL', label: t('inventory.allSites') },
            ...sites.map((site) => ({ value: site.id, label: `${site.name} · ${site.region}` })),
          ]}
        />
      </Space>

      <DataTable<Equipment>
        queryKey={[...queryKeys.inventory.equipment, filter]}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="inventoryEquipment"
        /* Amallar ustuni 100 dan 56 ga qisqardi */
        scrollX={1156}
        stickyFirstColumn
        searchable
        searchPlaceholder={t('inventory.searchPlaceholder')}
        emptyTitle={t('inventory.empty')}
        /*
          ⚠️ Bo'sh ro'yxat «jihoz yo'q» degani EMAS: hisobdan
          chiqarilganlar sukut bo'yicha yashirin.
        */
        {...(hiddenCounts && hiddenCounts.hidden > 0
          ? {
              emptyDescription: t('dataTable.closedHiddenHint'),
              emptyAction: { label: t('dataTable.stateAll'), onClick: () => changeState(STATE_ALL) },
            }
          : { emptyDescription: t('inventory.emptyHint') })}
        onRow={(row) => ({ style: { cursor: 'pointer' }, onClick: () => setSelected(row) })}
      />

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        width={520}
        title={selected?.serialNumber ?? ''}
        extra={
          selected ? (
            <Can permission="inventory.equipment.write">
              <Button
                size="small"
                onClick={() => {
                  setEditing(selected)
                  setFormOpen(true)
                }}
              >
                {t('inventory.edit')}
              </Button>
            </Can>
          ) : null
        }
      >
        {selected ? (
          <>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label={t('inventory.type')}>
                {t(`equipmentType.${selected.type}`)}
              </Descriptions.Item>
              <Descriptions.Item label={t('inventory.model')}>
                {selected.model ?? t('common.dash')}
              </Descriptions.Item>
              <Descriptions.Item label={t('inventory.state')}>
                <Tag color={STATE_COLOR[selected.state]}>
                  {t(`equipmentState.${selected.state}`)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('inventory.site')}>
                <SiteRef siteId={selected.siteId} />
              </Descriptions.Item>
              {/* ⚠️ Joylashuvdan ALOHIDA qator: ombor — TASHKILIY egalik,
                  joylashuv esa FIZIK joy. Bittasini ko'rsatib ikkinchisini
                  yashirsak, "jihoz qayerda" degan savolga yarim javob
                  bo'lardi (`Equipment` javadoc'i). */}
              <Descriptions.Item label={t('inventory.warehouse')}>
                <WarehouseRef nodeId={selected.nodeId} />
              </Descriptions.Item>
              <Descriptions.Item label={t('inventory.owner')}>
                <EquipmentOwnerRef ownerType={selected.ownerType} ownerId={selected.ownerId} />
              </Descriptions.Item>
              <Descriptions.Item label={t('inventory.assignedAt')}>
                {selected.assignedAt ? (
                  <Typography.Text title={utcTooltip(selected.assignedAt)}>
                    {formatDateTime(selected.assignedAt)}
                  </Typography.Text>
                ) : (
                  t('common.dash')
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('inventory.notes')}>
                {selected.notes ?? t('common.dash')}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ height: 24 }} />

            <Typography.Title level={5}>{t('actions.sectionTitle')}</Typography.Title>
            <EquipmentActions
              equipment={selected}
              onDone={() => setSelected(null)}
              onChangeOwner={() => {
                setOwnerTarget(selected)
                setOwnerOpen(true)
              }}
            />
          </>
        ) : null}
      </Drawer>

      <EquipmentFormModal
        open={formOpen}
        equipment={editing}
        // XSB-003: tekshiruvchi egani AYNAN shu oynada qidirdi. Tahrirlash
        // `PUT` i egani olmaydi (u alohida amal va alohida audit yozuvi),
        // shuning uchun bu yerda maydon emas, o'sha oynani ochadigan tugma.
        onChangeOwner={
          editing
            ? () => {
                setFormOpen(false)
                setOwnerTarget(editing)
                setOwnerOpen(true)
              }
            : undefined
        }
        onClose={() => {
          setFormOpen(false)
          // Kartochka ochiq qolsa eskirgan nusxani ko'rsatib turardi
          setSelected(null)
        }}
      />

      <EquipmentOwnerModal
        open={ownerOpen}
        equipment={ownerTarget}
        onClose={() => {
          setOwnerOpen(false)
          setOwnerTarget(null)
          // Kartochkadagi nusxa endi eskirgan — `Equipment` obyekti
          // so'rovdan keladi, uni qo'lda yamash ikki haqiqat yaratardi
          setSelected(null)
        }}
      />
    </>
  )
}
